// @flow
import { set, get, omit } from 'lodash';

const toSchemaKey = path => {
    return `properties.${path.split('.').join('.properties.')}`;
};

const toScope = path => {
    return `#/${toSchemaKey(path).split('.').join('/')}`;
};

const isAllowNullType = (type: string) => {
    return ['string', 'boolean'].includes(type);
};

// Extracts the module or section name as id from the node label.
const generateId = node => {
    const { label, type } = node;

    if (type !== 'Tab') {
        return '';
    }

    const parts = label.split('.');

    const moduleIdx = parts.findIndex(part => part === 'modules') + 1;
    if (moduleIdx > 0) {
        return parts[moduleIdx];
    }

    const sectionIdx = parts.findIndex(part => part === 'sections') + 1;
    if (sectionIdx > 0) {
        return parts[sectionIdx];
    }

    return label;
};

const updateSchema = (schema, data, options) => {
    const key = toSchemaKey(data.path);
    set(schema, key, {
        ...omit(data, ['path', 'required']),
        // by default show description, if explicitly disabled then don't show
        description: options?.description === false ? undefined : 'description',
        type:
            // if not required then allow null values
            !data.required && isAllowNullType(data.type)
                ? [data.type, 'null']
                : data.type
    });

    if (data.required) {
        const splittedPath = data.path.split('.');
        const parentKey = toSchemaKey(splittedPath.slice(0, -1).join('.'));
        const property = splittedPath[splittedPath.length - 1];
        const value = get(schema, parentKey);
        set(schema, parentKey, {
            ...value,
            required: [...(value.required || []), property]
        });
    }
};

const genRuleCondition = condition => {
    if (condition?.path) {
        return {
            ...omit(condition, 'path'),
            scope: toScope(condition.path)
        };
    }

    if (condition?.conditions) {
        return {
            ...condition,
            conditions: condition.conditions.map(genRuleCondition)
        };
    }

    return condition;
};

const genRule = rule => {
    if (rule?.condition) {
        return {
            ...rule,
            condition: genRuleCondition(rule.condition)
        };
    }

    return rule;
};

const parse = (node, schema = {}) => {
    if (node.elements) {
        return {
            ...node,
            id: generateId(node),
            rule: genRule(node.rule),
            elements: node.elements.map(element => {
                return parse(element, schema);
            })
        };
    }

    if (!node.data) {
        // throw new Error('Expecting data to be present');
        return node;
    }

    const { data } = node;

    updateSchema(schema, data, node.options);

    return {
        ...omit(node, 'data'),
        scope: toScope(data.path),
        rule: genRule(node.rule)
    };
};

const parseMetadata = (
    metadata: $FixMe
): { schema: $FixMe, uiSchema: $FixMe } => {
    const schema = {};
    const uiSchema = parse(metadata, schema);
    return { schema, uiSchema };
};

export const generateKeyFromId = (id: string): string => {
    const parts = id.split('.');
    const key = parts.slice(0, -2);
    const [field, name] = parts.slice(-2);
    return `${key.join('.fields.')}.fields.${field}.${name}`;
};

export default parseMetadata;
